import { authenticatedFetch } from "@lumi/data";
import { useEffect, useState } from "react";
import { useAuth } from "../../../auth/AuthContext";
import { createRefreshCallback } from "../../../auth/refreshCallback";

/**
 * Submission status enum matching backend
 */
export type SubmissionStatus = "DRAFT" | "PENDING" | "APPROVED" | "REJECTED" | "PUBLISHED";

/**
 * Submission data structure
 */
export interface SubmissionAsset {
  id: string;
  kind: string;
  rawFileSize: string;
  rawFileName: string | null;
  encodingStatus: string;
  encodingCost: string;
  encodingError: string | null;
  hlsUrl: string | null;
  dashUrl: string | null;
  thumbnailUrl: string | null;
  durationSec: number | null;
  streamVideoId: string | null;
  // R2 metadata fields
  uploadedBy?: string | null;
  contentType?: string | null;
}

export interface Submission {
  id: string;
  title: string;
  slug: string;
  year: number | null; // Actual DB field name
  durationMinutes: number | null; // Actual DB field name
  synopsis: string | null;
  description: string | null;
  directorData: unknown; // JSON field
  castData: unknown; // JSON field
  crewData: unknown; // JSON field
  genreData: unknown; // JSON field
  rating: string | null;
  languages: string[];
  country: string | null;
  status: SubmissionStatus;
  tmdbId: number | null;
  posterUrl: string | null;
  backdropUrl: string | null;
  logoUrl: string | null;
  horizontalTileUrl: string | null;
  verticalTileUrl: string | null;
  createdAt: string;
  updatedAt: string;
  submittedAt: string | null;
  reviewedAt: string | null;
  publishedAt: string | null;
  reviewNotes: string | null;
  assets?: SubmissionAsset[];
  /** Raw metadata JSON from API - contains subtitle data */
  metadata?: SubmissionMetadata | null;
  // Legacy fields - prefer extracting from metadata
  featureSubtitles?: SubtitleMetadata[];
  trailerSubtitles?: SubtitleMetadata[];

  // Keep legacy fields for backward compatibility with forms
  releaseYear?: number | null;
  runtime?: number | null;
  director?: string | null;
  cast?: string[];
  genres?: string[];
}

/** Subtitle metadata stored in submission.metadata */
interface SubtitleMetadata {
  language: string;
  label: string;
  r2Key: string;
  fileName: string;
  fileSize?: number;
  /** Original format before conversion */
  format?: "VTT" | "SRT";
  processing?: {
    cueCount: number;
    sizeKB: number;
    wasConverted: boolean;
    wasNormalized: boolean;
    timecodeOffset: number;
  };
  /** Validation results from subtitle processing */
  validation?: {
    valid: boolean;
    errors: string[];
    /** Detailed warnings (individual cue messages) */
    warnings: string[];
    /** Grouped warnings for compact tooltip display */
    warningsSummary?: string[];
  };
}

/** Metadata JSON structure stored in submission.metadata */
interface SubmissionMetadata {
  featureSubtitles?: SubtitleMetadata[];
  trailerSubtitles?: SubtitleMetadata[];
}

/**
 * Paginated response for submissions list
 */
export interface SubmissionsResponse {
  submissions: Submission[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Hook for fetching submissions list with filters and pagination
 */
export function useSubmissions(status?: SubmissionStatus, page = 1, pageSize = 20) {
  const { accessToken, refreshAuth } = useAuth();
  const [data, setData] = useState<SubmissionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  useEffect(() => {
    if (!accessToken) {
      setLoading(false);
      setError(new Error("Not authenticated. Please log in."));
      return;
    }

    const fetchSubmissions = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams({
          page: page.toString(),
          pageSize: pageSize.toString(),
        });

        if (status) {
          params.append("status", status);
        }

        const url = `${import.meta.env.VITE_API_BASE_URL || "http://localhost:3333"}/v1/provider/submissions?${params.toString()}`;

        const result = await authenticatedFetch<SubmissionsResponse>(
          url,
          { method: "GET" },
          accessToken,
          createRefreshCallback(refreshAuth),
        );

        setData(result);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Unknown error";
        setError(new Error(errorMessage));
        console.error("Error loading submissions:", errorMessage);
      } finally {
        setLoading(false);
      }
    };

    void fetchSubmissions();
  }, [accessToken, refreshAuth, status, page, pageSize, refetchTrigger]);

  const refetch = () => setRefetchTrigger((prev) => prev + 1);

  return { data, loading, error, refetch };
}

/**
 * Hook for fetching a single submission by ID
 */
export function useSubmission(id: string | undefined) {
  const { accessToken, refreshAuth } = useAuth();
  const [data, setData] = useState<Submission | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!accessToken || !id) {
      setLoading(false);
      if (!accessToken) {
        setError(new Error("Not authenticated. Please log in."));
      }
      return;
    }

    const fetchSubmission = async () => {
      try {
        setLoading(true);
        setError(null);

        const url = `${import.meta.env.VITE_API_BASE_URL || "http://localhost:3333"}/v1/provider/submissions/${id}`;

        const result = await authenticatedFetch<{ submission: Submission }>(
          url,
          { method: "GET" },
          accessToken,
          createRefreshCallback(refreshAuth),
        );

        setData(result.submission);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Unknown error";
        setError(new Error(errorMessage));
        console.error("Error loading submission:", errorMessage);
      } finally {
        setLoading(false);
      }
    };

    void fetchSubmission();
  }, [accessToken, refreshAuth, id]);

  return { data, loading, error, refetch: () => setData(null) };
}
